import { createContextualCan, useAbility as useCaslAbility } from '@casl/react';
import type { ReactNode } from 'react';
import { createContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
	defineAbilityFor,
	defineFallbackAbility,
} from '@adflow/shared/util-auth';
import type { permissionSystem } from '@adflow/shared/util-permissions';

import { useAuth } from './use-auth';

const AbilityContext = createContext(defineFallbackAbility());

export interface AbilityProviderProps {
	children?: ReactNode;
}

export function AbilityProvider({ children }: AbilityProviderProps) {
	const { user } = useAuth();

	const ability = useMemo<permissionSystem.Ability>(
		() => defineAbilityFor(user),
		[user]
	);

	return (
		<AbilityContext.Provider value={ability}>
			{children}
		</AbilityContext.Provider>
	);
}

/**
 * Returns the ability object of the current authenticated user for checking permissions
 * and a string generator for when a user is lacking a permission
 * See https://casl.js.org/v4/en/guide/intro#conditions for examples.
 * This is used instead of `@casl/react`'s useAbility because their ability requires
 * the context to be imported
 */
export function useAbility() {
	const ability = useCaslAbility(AbilityContext);
	const { t } = useTranslation('permissions');
	const missingPermissionText = useCallback(
		(
			action: permissionSystem.ActionType,
			permission: permissionSystem.PermissionType
		): string =>
			t('no permission', {
				action: t(`action ${action}`),
				permission: t(`type ${permission}`),
			}),
		[t]
	);
	return {
		ability,
		missingPermissionText,
	};
}

export const Can = createContextualCan<permissionSystem.Ability>(
	AbilityContext.Consumer
);
